// @ts-nocheck
import { useState, useEffect, useCallback } from 'react'
import {
  MessageCircle, Send, BarChart3, Clock, Check, X,
  RefreshCw, Users, CircleDot, Megaphone, Bot, FileText, Plus, Pencil, Trash2, Sparkles, Eye, EyeOff,
} from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useLineChatStore } from '../stores/lineChatStore'

type Tab = 'auto-response' | 'broadcast' | 'statistics' | 'templates' | 'ai-assist'

interface AutoResponseMessage {
  enabled: boolean
  message: string
}

interface AutoResponseSettings {
  followMessage: AutoResponseMessage
  businessHoursMessage: AutoResponseMessage
  outsideBusinessHoursMessage: AutoResponseMessage
  businessHoursStart: string
  businessHoursEnd: string
  businessDays: number[]
}

interface Statistics {
  totalConversations: number
  activeConversations: number
  inactiveConversations: number
  statusCounts: { unhandled: number; in_progress: number; done: number }
  totalUnread: number
  operatorCounts: Record<string, number>
  unassignedCount: number
  today: { totalMessages: number; incoming: number; outgoing: number }
}

interface LineAiConfig {
  provider: 'claude' | 'openai'
  apiKey: string
  model: string
  defaultTone: string
  systemPrompt: string
}

interface LineTemplate {
  templateId: string
  title: string
  content: string
  category: string | null
  createdAt: string
  updatedAt: string
}

const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

export default function LineSettingsPage() {
  const api = useApi()
  const [activeTab, setActiveTab] = useState<Tab>('auto-response')

  // Auto-response state
  const [autoResponse, setAutoResponse] = useState<AutoResponseSettings>({
    followMessage: { enabled: false, message: '' },
    businessHoursMessage: { enabled: false, message: '' },
    outsideBusinessHoursMessage: { enabled: false, message: '' },
    businessHoursStart: '09:00',
    businessHoursEnd: '18:00',
    businessDays: [1, 2, 3, 4, 5],
  })
  const [arLoading, setArLoading] = useState(true)
  const [arSaving, setArSaving] = useState(false)
  const [arSaved, setArSaved] = useState(false)

  // Broadcast state
  const conversations = useLineChatStore(s => s.conversations)
  const [broadcastText, setBroadcastText] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [broadcastSending, setBroadcastSending] = useState(false)
  const [broadcastResult, setBroadcastResult] = useState<{ successCount: number; failCount: number; skippedCount?: number } | null>(null)
  const [broadcastSearchQuery, setBroadcastSearchQuery] = useState('')

  // Statistics state
  const [stats, setStats] = useState<Statistics | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)

  // Operators for stats display
  const [operators, setOperators] = useState<{ userId: string; displayName?: string; name?: string }[]>([])

  // Template state
  const [templates, setTemplates] = useState<LineTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<LineTemplate | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [templateForm, setTemplateForm] = useState({ title: '', content: '', category: '' })
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateDeleting, setTemplateDeleting] = useState<string | null>(null)

  // AI assist state
  const [aiConfig, setAiConfig] = useState<LineAiConfig>({
    provider: 'claude',
    apiKey: '',
    model: '',
    defaultTone: '丁寧',
    systemPrompt: '',
  })
  const [aiLoading, setAiLoading] = useState(true)
  const [aiSaving, setAiSaving] = useState(false)
  const [aiSaved, setAiSaved] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)

  // Load auto-response settings
  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.get('/api/line/auto-response')
        if (data.autoResponse) {
          setAutoResponse(data.autoResponse)
        }
      } catch (error) {
        console.error('自動応答設定の取得に失敗:', error)
      } finally {
        setArLoading(false)
      }
    }
    load()
  }, [api])

  // Load operators
  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.get('/api/users')
        setOperators(data.items ?? [])
      } catch (error) {
        console.error('オペレーター取得に失敗:', error)
      }
    }
    load()
  }, [api])

  // Load all conversations for broadcast（全件をページネーションで取得）
  useEffect(() => {
    const loadAllConversations = async () => {
      try {
        const allConvs: typeof conversations = []
        let cursor: string | undefined
        do {
          const url = cursor
            ? `/api/line/conversations?limit=200&cursor=${encodeURIComponent(cursor)}`
            : '/api/line/conversations?limit=200'
          const data = await api.get(url)
          allConvs.push(...(data.conversations || []))
          cursor = data.nextCursor
        } while (cursor)
        useLineChatStore.getState().setConversations(allConvs)
      } catch (error) {
        console.error('トーク一覧取得に失敗:', error)
      }
    }
    if (conversations.length === 0) {
      loadAllConversations()
    }
  }, [api, conversations.length])

  // Save auto-response
  const handleSaveAutoResponse = async () => {
    setArSaving(true)
    setArSaved(false)
    try {
      await api.put('/api/line/auto-response', autoResponse)
      setArSaved(true)
      setTimeout(() => setArSaved(false), 3000)
    } catch (error) {
      console.error('自動応答設定の保存に失敗:', error)
    } finally {
      setArSaving(false)
    }
  }

  // Toggle business day
  const toggleDay = (day: number) => {
    const days = autoResponse.businessDays
    if (days.includes(day)) {
      setAutoResponse({ ...autoResponse, businessDays: days.filter(d => d !== day) })
    } else {
      setAutoResponse({ ...autoResponse, businessDays: [...days, day].sort() })
    }
  }

  const updateMessageType = (key: 'followMessage' | 'businessHoursMessage' | 'outsideBusinessHoursMessage', field: 'enabled' | 'message', value: boolean | string) => {
    setAutoResponse({
      ...autoResponse,
      [key]: { ...autoResponse[key], [field]: value },
    })
  }

  // Broadcast
  const handleBroadcast = async () => {
    if (!broadcastText.trim() || selectedUserIds.length === 0) return
    setBroadcastSending(true)
    setBroadcastResult(null)
    try {
      const data = await api.post('/api/line/broadcast', {
        lineUserIds: selectedUserIds,
        text: broadcastText.trim(),
      })
      setBroadcastResult({ successCount: data.successCount, failCount: data.failCount, skippedCount: data.skippedCount })
      if (data.failCount === 0) {
        setBroadcastText('')
        setSelectedUserIds([])
      }
    } catch (error) {
      console.error('一括送信に失敗:', error)
    } finally {
      setBroadcastSending(false)
    }
  }

  const toggleAllUsers = () => {
    const activeUsers = conversations.filter(c => c.status === 'active').map(c => c.lineUserId)
    if (selectedUserIds.length === activeUsers.length) {
      setSelectedUserIds([])
    } else {
      setSelectedUserIds(activeUsers)
    }
  }

  // Statistics
  const loadStatistics = useCallback(async () => {
    setStatsLoading(true)
    try {
      const data = await api.get('/api/line/statistics')
      setStats(data.statistics)
    } catch (error) {
      console.error('統計の取得に失敗:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [api])

  useEffect(() => {
    if (activeTab === 'statistics') {
      loadStatistics()
    }
  }, [activeTab, loadStatistics])

  // Load templates
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true)
    try {
      const data = await api.get('/api/line/templates')
      setTemplates(data.templates)
    } catch (error) {
      console.error('テンプレート取得に失敗:', error)
    } finally {
      setTemplatesLoading(false)
    }
  }, [api])

  useEffect(() => {
    if (activeTab === 'templates') {
      loadTemplates()
    }
  }, [activeTab, loadTemplates])

  const handleCreateTemplate = async () => {
    if (!templateForm.title.trim() || !templateForm.content.trim()) return
    setTemplateSaving(true)
    try {
      const created = await api.post('/api/line/templates', {
        title: templateForm.title.trim(),
        content: templateForm.content.trim(),
        category: templateForm.category.trim() || null,
      })
      setTemplates(prev => [...prev, created])
      setTemplateForm({ title: '', content: '', category: '' })
      setIsCreating(false)
    } catch (error) {
      console.error('テンプレート作成に失敗:', error)
    } finally {
      setTemplateSaving(false)
    }
  }

  const handleUpdateTemplate = async () => {
    if (!editingTemplate || !templateForm.title.trim() || !templateForm.content.trim()) return
    setTemplateSaving(true)
    try {
      await api.put(`/api/line/templates/${editingTemplate.templateId}`, {
        title: templateForm.title.trim(),
        content: templateForm.content.trim(),
        category: templateForm.category.trim() || null,
      })
      setTemplates(prev => prev.map(t =>
        t.templateId === editingTemplate.templateId
          ? { ...t, title: templateForm.title.trim(), content: templateForm.content.trim(), category: templateForm.category.trim() || null, updatedAt: new Date().toISOString() }
          : t
      ))
      setEditingTemplate(null)
      setTemplateForm({ title: '', content: '', category: '' })
    } catch (error) {
      console.error('テンプレート更新に失敗:', error)
    } finally {
      setTemplateSaving(false)
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    setTemplateDeleting(templateId)
    try {
      await api.delete(`/api/line/templates/${templateId}`)
      setTemplates(prev => prev.filter(t => t.templateId !== templateId))
    } catch (error) {
      console.error('テンプレート削除に失敗:', error)
    } finally {
      setTemplateDeleting(null)
    }
  }

  const startEdit = (template: LineTemplate) => {
    setEditingTemplate(template)
    setIsCreating(false)
    setTemplateForm({ title: template.title, content: template.content, category: template.category ?? '' })
  }

  const startCreate = () => {
    setEditingTemplate(null)
    setIsCreating(true)
    setTemplateForm({ title: '', content: '', category: '' })
  }

  const cancelTemplateForm = () => {
    setEditingTemplate(null)
    setIsCreating(false)
    setTemplateForm({ title: '', content: '', category: '' })
  }

  // Load AI config
  const loadAiConfig = useCallback(async () => {
    setAiLoading(true)
    try {
      const data = await api.get('/api/line/ai-config')
      if (data.aiConfig) {
        setAiConfig({
          provider: data.aiConfig.provider ?? 'claude',
          apiKey: data.aiConfig.apiKey ?? '',
          model: data.aiConfig.model ?? '',
          defaultTone: data.aiConfig.defaultTone ?? '丁寧',
          systemPrompt: data.aiConfig.systemPrompt ?? '',
        })
      }
    } catch (error) {
      console.error('AI設定の取得に失敗:', error)
    } finally {
      setAiLoading(false)
    }
  }, [api])

  useEffect(() => {
    if (activeTab === 'ai-assist') {
      loadAiConfig()
    }
  }, [activeTab, loadAiConfig])

  // Save AI config
  const handleSaveAiConfig = async () => {
    setAiSaving(true)
    setAiSaved(false)
    try {
      await api.put('/api/line/ai-config', aiConfig)
      setAiSaved(true)
      setShowApiKey(false)
      setTimeout(() => setAiSaved(false), 3000)
    } catch (error) {
      console.error('AI設定の保存に失敗:', error)
    } finally {
      setAiSaving(false)
    }
  }

  const tabs: { key: Tab; label: string; icon: typeof Bot }[] = [
    { key: 'auto-response', label: '自動応答', icon: Bot },
    { key: 'templates', label: 'テンプレート', icon: FileText },
    { key: 'broadcast', label: '一括送信', icon: Megaphone },
    { key: 'statistics', label: '統計', icon: BarChart3 },
    { key: 'ai-assist', label: 'AI返信', icon: Sparkles },
  ]

  return (
    <div className="max-w-4xl">
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-white dark:bg-gray-700 text-green-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Auto-response Tab */}
      {activeTab === 'auto-response' && (
        <div className="space-y-4">
          {arLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* 1. フォローメッセージ */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">フォローメッセージ</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      友だち追加時に自動で送信するメッセージ
                    </p>
                  </div>
                  <button
                    onClick={() => updateMessageType('followMessage', 'enabled', !autoResponse.followMessage.enabled)}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      autoResponse.followMessage.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${
                      autoResponse.followMessage.enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                {autoResponse.followMessage.enabled && (
                  <textarea
                    value={autoResponse.followMessage.message}
                    onChange={(e) => updateMessageType('followMessage', 'message', e.target.value)}
                    rows={3}
                    placeholder="例: 友だち追加ありがとうございます！お気軽にメッセージをお送りください。"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white resize-none"
                  />
                )}
              </div>

              {/* 2. 営業時間内メッセージ */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">営業時間内メッセージ</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      営業時間内に受信したメッセージへの自動応答
                    </p>
                  </div>
                  <button
                    onClick={() => updateMessageType('businessHoursMessage', 'enabled', !autoResponse.businessHoursMessage.enabled)}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      autoResponse.businessHoursMessage.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${
                      autoResponse.businessHoursMessage.enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                {autoResponse.businessHoursMessage.enabled && (
                  <textarea
                    value={autoResponse.businessHoursMessage.message}
                    onChange={(e) => updateMessageType('businessHoursMessage', 'message', e.target.value)}
                    rows={3}
                    placeholder="例: お問い合わせありがとうございます。担当者が確認次第、ご連絡いたします。"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white resize-none"
                  />
                )}
              </div>

              {/* 3. 営業時間外メッセージ */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">営業時間外メッセージ</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      営業時間外に受信したメッセージへの自動応答
                    </p>
                  </div>
                  <button
                    onClick={() => updateMessageType('outsideBusinessHoursMessage', 'enabled', !autoResponse.outsideBusinessHoursMessage.enabled)}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      autoResponse.outsideBusinessHoursMessage.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${
                      autoResponse.outsideBusinessHoursMessage.enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                {autoResponse.outsideBusinessHoursMessage.enabled && (
                  <textarea
                    value={autoResponse.outsideBusinessHoursMessage.message}
                    onChange={(e) => updateMessageType('outsideBusinessHoursMessage', 'message', e.target.value)}
                    rows={3}
                    placeholder="例: お問い合わせありがとうございます。ただいま営業時間外のため、翌営業日にご連絡いたします。"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white resize-none"
                  />
                )}
              </div>

              {/* 営業時間設定（共通） */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">営業時間設定</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">営業時間（JST）</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={autoResponse.businessHoursStart}
                        onChange={(e) => setAutoResponse({ ...autoResponse, businessHoursStart: e.target.value })}
                        className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg dark:text-white"
                      />
                      <span className="text-gray-500">〜</span>
                      <input
                        type="time"
                        value={autoResponse.businessHoursEnd}
                        onChange={(e) => setAutoResponse({ ...autoResponse, businessHoursEnd: e.target.value })}
                        className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">営業日</label>
                    <div className="flex gap-1">
                      {DAY_LABELS.map((label, i) => (
                        <button
                          key={i}
                          onClick={() => toggleDay(i)}
                          className={`w-9 h-9 rounded-full text-xs font-medium transition-colors ${
                            autoResponse.businessDays.includes(i)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 保存ボタン */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveAutoResponse}
                    disabled={arSaving}
                    className="px-6 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    {arSaving ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    保存
                  </button>
                  {arSaved && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      保存しました
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Broadcast Tab */}
      {activeTab === 'broadcast' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-6">
            {/* メッセージ入力 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                一括送信メッセージ
              </label>
              <textarea
                value={broadcastText}
                onChange={(e) => setBroadcastText(e.target.value)}
                rows={4}
                placeholder="すべての選択したLINEユーザーに送信するメッセージを入力..."
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white resize-none"
              />
            </div>

            {/* 送信先選択 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  送信先（{selectedUserIds.length}件選択 / {conversations.filter(c => c.status === 'active').length}件）
                </label>
                <button
                  onClick={toggleAllUsers}
                  className="text-xs text-green-600 hover:text-green-700"
                >
                  {selectedUserIds.length === conversations.filter(c => c.status === 'active').length
                    ? 'すべて解除'
                    : 'すべて選択'}
                </button>
              </div>
              {/* 送信先検索 */}
              <input
                type="text"
                value={broadcastSearchQuery}
                onChange={(e) => setBroadcastSearchQuery(e.target.value)}
                placeholder="名前で検索..."
                className="w-full mb-2 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
              />
              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                {(() => {
                  const activeConvs = conversations.filter(c => c.status === 'active')
                  const filtered = broadcastSearchQuery.trim()
                    ? activeConvs.filter(c => (c.displayName || '').toLowerCase().includes(broadcastSearchQuery.toLowerCase().trim()))
                    : activeConvs
                  if (filtered.length === 0) {
                    return (
                      <div className="px-3 py-8 text-center text-sm text-gray-400">
                        {broadcastSearchQuery.trim() ? '該当するユーザーがいません' : 'アクティブなトークがありません'}
                      </div>
                    )
                  }
                  return filtered.map((conv) => (
                    <label
                      key={conv.lineUserId}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(conv.lineUserId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUserIds([...selectedUserIds, conv.lineUserId])
                          } else {
                            setSelectedUserIds(selectedUserIds.filter(id => id !== conv.lineUserId))
                          }
                        }}
                        className="w-4 h-4 text-green-500 border-gray-300 rounded focus:ring-green-500"
                      />
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {conv.pictureUrl ? (
                          <img src={conv.pictureUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-sm text-gray-900 dark:text-white truncate">
                        {conv.displayName || 'LINE ユーザー'}
                      </span>
                    </label>
                  ))
                })()}
              </div>
            </div>

            {/* 送信ボタン + 結果 */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleBroadcast}
                disabled={broadcastSending || !broadcastText.trim() || selectedUserIds.length === 0}
                className="px-6 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                {broadcastSending ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {selectedUserIds.length}件に送信
              </button>
              {broadcastResult && (
                <span className={`text-sm flex items-center gap-1 ${
                  broadcastResult.failCount > 0 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  <Check className="w-4 h-4" />
                  {broadcastResult.successCount}件成功
                  {broadcastResult.failCount > 0 && `, ${broadcastResult.failCount}件失敗`}
                  {broadcastResult.skippedCount ? `, ${broadcastResult.skippedCount}件スキップ（ブロック済み）` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {/* ヘッダー + 新規作成ボタン */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              チャットで使用できる定型文テンプレートを管理します
            </p>
            {!isCreating && !editingTemplate && (
              <button
                onClick={startCreate}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                新規作成
              </button>
            )}
          </div>

          {/* 作成/編集フォーム */}
          {(isCreating || editingTemplate) && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                {isCreating ? 'テンプレート新規作成' : 'テンプレート編集'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">タイトル</label>
                  <input
                    type="text"
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                    placeholder="例: 営業時間のご案内"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">カテゴリ（任意）</label>
                  <input
                    type="text"
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    placeholder="例: 挨拶、案内、FAQ"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">本文</label>
                  <textarea
                    value={templateForm.content}
                    onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                    rows={5}
                    placeholder="テンプレートの本文を入力..."
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white resize-none"
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={isCreating ? handleCreateTemplate : handleUpdateTemplate}
                    disabled={templateSaving || !templateForm.title.trim() || !templateForm.content.trim()}
                    className="px-5 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    {templateSaving ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {isCreating ? '作成' : '更新'}
                  </button>
                  <button
                    onClick={cancelTemplateForm}
                    className="px-5 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* テンプレート一覧 */}
          {templatesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full" />
            </div>
          ) : templates.length === 0 && !isCreating ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">テンプレートがありません</p>
              <button
                onClick={startCreate}
                className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium"
              >
                最初のテンプレートを作成する
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <div
                  key={template.templateId}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {template.title}
                        </h4>
                        {template.category && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full flex-shrink-0">
                            {template.category}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap line-clamp-3">
                        {template.content}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => startEdit(template)}
                        className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="編集"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.templateId)}
                        disabled={templateDeleting === template.templateId}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="削除"
                      >
                        {templateDeleting === template.templateId ? (
                          <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AI Assist Tab */}
      {activeTab === 'ai-assist' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {aiLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">AI返信アシスト設定</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  LINEチャットでAIによる返信案の生成を利用するための設定です
                </p>
              </div>

              {/* プロバイダー選択 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  AIプロバイダー
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ai-provider"
                      checked={aiConfig.provider === 'claude'}
                      onChange={() => setAiConfig({ ...aiConfig, provider: 'claude', model: aiConfig.provider === 'claude' ? aiConfig.model : '' })}
                      className="w-4 h-4 text-green-500 border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Claude（Anthropic）</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ai-provider"
                      checked={aiConfig.provider === 'openai'}
                      onChange={() => setAiConfig({ ...aiConfig, provider: 'openai', model: aiConfig.provider === 'openai' ? aiConfig.model : '' })}
                      className="w-4 h-4 text-green-500 border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">ChatGPT（OpenAI）</span>
                  </label>
                </div>
              </div>

              {/* APIキー */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  APIキー
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={aiConfig.apiKey}
                    onChange={(e) => setAiConfig({ ...aiConfig, apiKey: e.target.value })}
                    placeholder={aiConfig.provider === 'claude' ? 'sk-ant-...' : 'sk-...'}
                    className="w-full px-3 py-2 pr-10 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {aiConfig.apiKey === '***masked***' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    APIキーは設定済みです。変更する場合は新しいキーを入力してください。
                  </p>
                )}
              </div>

              {/* モデル */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  モデル
                </label>
                <input
                  type="text"
                  value={aiConfig.model}
                  onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                  placeholder={aiConfig.provider === 'claude' ? 'claude-sonnet-4-20250514' : 'gpt-4o'}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {aiConfig.provider === 'claude'
                    ? '例: claude-sonnet-4-20250514, claude-haiku-4-5-20251001'
                    : '例: gpt-4o, gpt-4o-mini'}
                </p>
              </div>

              {/* デフォルトトーン */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  デフォルトのトーン
                </label>
                <input
                  type="text"
                  value={aiConfig.defaultTone}
                  onChange={(e) => setAiConfig({ ...aiConfig, defaultTone: e.target.value })}
                  placeholder="丁寧"
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  返信生成時のデフォルトのトーン（例: 丁寧、カジュアル、ビジネス）
                </p>
              </div>

              {/* カスタムシステムプロンプト */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  カスタムシステムプロンプト（任意）
                </label>
                <textarea
                  value={aiConfig.systemPrompt}
                  onChange={(e) => setAiConfig({ ...aiConfig, systemPrompt: e.target.value })}
                  rows={4}
                  placeholder="例: お客様には必ず敬語を使ってください。商品の返品期限は30日です。..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white resize-y"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  組織固有の指示やルールをAIに伝えることができます
                </p>
              </div>

              {/* 保存ボタン */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSaveAiConfig}
                  disabled={aiSaving || !aiConfig.apiKey || !aiConfig.model}
                  className="px-6 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  {aiSaving ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  保存
                </button>
                {aiSaved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    保存しました
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && (
        <div className="space-y-4">
          {/* リロードボタン */}
          <div className="flex justify-end">
            <button
              onClick={loadStatistics}
              disabled={statsLoading}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-green-600 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} />
              更新
            </button>
          </div>

          {statsLoading && !stats ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full" />
            </div>
          ) : stats ? (
            <>
              {/* サマリーカード */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="総トーク数" value={stats.totalConversations} icon={MessageCircle} color="green" />
                <StatCard label="アクティブ" value={stats.activeConversations} icon={Users} color="blue" />
                <StatCard label="未読合計" value={stats.totalUnread} icon={MessageCircle} color="red" />
                <StatCard label="今日の受信" value={stats.today.incoming} icon={MessageCircle} color="yellow" />
              </div>

              {/* ステータス別 */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">対応ステータス</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <CircleDot className="w-5 h-5 text-red-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-red-600">{stats.statusCounts.unhandled}</p>
                    <p className="text-xs text-gray-500">未対応</p>
                  </div>
                  <div className="text-center">
                    <Clock className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-yellow-600">{stats.statusCounts.in_progress}</p>
                    <p className="text-xs text-gray-500">対応中</p>
                  </div>
                  <div className="text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-green-600">{stats.statusCounts.done}</p>
                    <p className="text-xs text-gray-500">完了</p>
                  </div>
                </div>
                {/* ステータスバー */}
                {stats.totalConversations > 0 && (
                  <div className="mt-4 h-3 flex rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                    <div
                      className="bg-red-400"
                      style={{ width: `${(stats.statusCounts.unhandled / stats.totalConversations) * 100}%` }}
                    />
                    <div
                      className="bg-yellow-400"
                      style={{ width: `${(stats.statusCounts.in_progress / stats.totalConversations) * 100}%` }}
                    />
                    <div
                      className="bg-green-400"
                      style={{ width: `${(stats.statusCounts.done / stats.totalConversations) * 100}%` }}
                    />
                  </div>
                )}
              </div>

              {/* 今日のメッセージ */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">今日のメッセージ</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.today.totalMessages}</p>
                    <p className="text-xs text-gray-500">合計</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{stats.today.incoming}</p>
                    <p className="text-xs text-gray-500">受信</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{stats.today.outgoing}</p>
                    <p className="text-xs text-gray-500">送信</p>
                  </div>
                </div>
              </div>

              {/* 担当者別 */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">担当者別トーク数</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">未割り当て</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.unassignedCount}</span>
                  </div>
                  {Object.entries(stats.operatorCounts).map(([opId, count]) => {
                    const op = operators.find(o => o.userId === opId)
                    return (
                      <div key={opId} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 dark:text-gray-300">
                          {op?.displayName ?? op?.name ?? opId}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">{count}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 py-12">
              統計データの取得に失敗しました
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, icon: Icon, color }: {
  label: string
  value: number
  icon: typeof MessageCircle
  color: 'green' | 'blue' | 'red' | 'yellow'
}) {
  const colorMap = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600',
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <div className={`w-8 h-8 rounded-lg ${colorMap[color]} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  )
}
